import { ref } from 'vue'
import type {
  IListaSqlConsultaParams,
  IListaSqlResultado,
  IListaSqlResultadoGrafica,
  TipoGraficaListaSql
} from '@/modules/Configuracion/interfaces'
import {
  normalizarResultadoGraficaListaSql,
  normalizarResultadoListaSql
} from '@/modules/Configuracion/utils/listaSql'
import { reporteService } from '@/services'

export const useListaSql = () => {
  const consultando = ref(false)
  const error = ref<string | null>(null)
  const resultado = ref<IListaSqlResultado | null>(null)
  const resultadoGrafica = ref<IListaSqlResultadoGrafica | null>(null)
  const hayMas = ref(false)
  let solicitudActual = 0

  const crearPeticion = ({ coreId, cittId }: IListaSqlConsultaParams) => ({
    coreId,
    parametros: [
      {
        corpNombre: 'P_CITT_ID',
        valor: cittId
      }
    ]
  })

  const cargarResultadoTabla = async (
    params: IListaSqlConsultaParams
  ): Promise<IListaSqlResultado> => {
    const solicitud = ++solicitudActual
    consultando.value = true
    error.value = null
    try {
      const { data } = await reporteService.execute(crearPeticion(params))
      const resultadoNormalizado = normalizarResultadoListaSql(data.body, params.label)
      if (solicitud === solicitudActual) {
        resultado.value = resultadoNormalizado
        hayMas.value = false
      }
      return resultadoNormalizado
    } catch (e) {
      if (solicitud === solicitudActual) {
        error.value = 'No fue posible consultar la información. Intenta nuevamente.'
      }
      throw e
    } finally {
      if (solicitud === solicitudActual) consultando.value = false
    }
  }

  const cargarVistaPreviaTabla = async (
    params: IListaSqlConsultaParams
  ): Promise<IListaSqlResultado> => {
    const solicitud = ++solicitudActual
    consultando.value = true
    error.value = null
    try {
      const { data } = await reporteService.executePaginated({
        ...crearPeticion(params),
        pagina: params.pagina ?? 1,
        limite: params.limite ?? 5
      })
      const resultadoNormalizado = normalizarResultadoListaSql(data.body.filas, params.label)
      if (solicitud === solicitudActual) {
        resultado.value = resultadoNormalizado
        hayMas.value = data.body.hayMas
      }
      return resultadoNormalizado
    } catch (e) {
      if (solicitud === solicitudActual) {
        error.value = 'No fue posible cargar la vista previa. Intenta nuevamente.'
      }
      throw e
    } finally {
      if (solicitud === solicitudActual) consultando.value = false
    }
  }

  const cargarResultadoGrafica = async (
    params: IListaSqlConsultaParams,
    tipoGrafica: TipoGraficaListaSql
  ): Promise<IListaSqlResultadoGrafica> => {
    const solicitud = ++solicitudActual
    consultando.value = true
    error.value = null
    try {
      const { data } = await reporteService.execute(crearPeticion(params))
      const resultadoNormalizado = normalizarResultadoGraficaListaSql(
        data.body,
        params.label,
        tipoGrafica
      )
      if (solicitud === solicitudActual) {
        resultadoGrafica.value = resultadoNormalizado
        hayMas.value = false
      }
      return resultadoNormalizado
    } catch (e) {
      if (solicitud === solicitudActual) {
        error.value = e instanceof Error && e.name === 'ErrorFormatoListaSql'
          ? e.message
          : 'No fue posible consultar la información. Intenta nuevamente.'
      }
      throw e
    } finally {
      if (solicitud === solicitudActual) consultando.value = false
    }
  }

  return {
    consultando,
    error,
    resultado,
    resultadoGrafica,
    hayMas,
    cargarResultadoTabla,
    cargarVistaPreviaTabla,
    cargarResultadoGrafica
  }
}
